// Type-only, so no runtime dependency runs from shared code back into the app layer.
import type { FeatureFlagId } from '@app/featureFlags/featureFlagRegistry';

/**
 * Local-development overrides for the flags the environment reports.
 *
 * Local development runs against a remote backend whose flags you cannot
 * change, so this is the only way to see what a capability looks like switched
 * the other way. Every gate in the app — routes, navigation, Authz filtering,
 * individual elements — reads the flags through one evaluation, so overriding
 * here applies app-wide.
 */

/**
 * For local development: force these flag ids to the given value, whatever the
 * environment says. An empty object means the environment is used as-is, and
 * an id that is absent here is left alone.
 *
 * ```ts
 * export const MOCK_FEATURE_FLAGS: Partial<Record<FeatureFlagId, boolean>> = {
 *   driveEnabled: false,
 *   workflowsActionsEnabled: true,
 * };
 * ```
 *
 * Only the flag itself is forced. A feature whose Discovery endpoint is absent
 * stays unreachable, because the override cannot conjure a service that is not
 * deployed — see the evaluation order.
 *
 * Read only under `import.meta.env.DEV`, so a forgotten entry is stripped from
 * a production build rather than switching a capability on in someone's
 * deployment.
 *
 * Keys are checked against the feature enums, so your editor completes them and
 * a misspelling fails the build instead of quietly doing nothing.
 *
 * Set back to {} before committing.
 */
export const MOCK_FEATURE_FLAGS: Partial<Record<FeatureFlagId, boolean>> = {};

/** Warned once per session, so an override left on does not go unnoticed for an afternoon. */
let hasWarned = false;

/**
 * Whether the id counts as enabled, after local overrides. Outside development
 * this is the environment's answer and nothing else.
 */
export const isFeatureFlagEnabled = (flagId: string, environmentFlags: Readonly<Record<string, boolean>>): boolean => {
  // Never under vitest (MODE === 'test'): specs assert closed-flag behavior, and an
  // active local override would fail them — pre-commit runs the related specs, so a
  // developer working with overrides on could not commit anything at all.
  if (import.meta.env.DEV && import.meta.env.MODE !== 'test') {
    const overrides = MOCK_FEATURE_FLAGS;

    if (!hasWarned && Object.keys(overrides).length > 0) {
      hasWarned = true;
      console.warn('[feature-flags] Local overrides are active; the environment is ignored for them.', overrides);
    }

    // `in`, not a truthy read: `false` is an override, and absent is not. The
    // evaluator's ids are plain strings, hence the widening on lookup.
    const value = (overrides as Record<string, boolean | undefined>)[flagId];
    if (flagId in overrides && value !== undefined) return value;
  }

  return environmentFlags[flagId] === true;
};
